import React, { useState, useEffect } from 'react';
import { Calendar, Package, Users } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import ManufacturingService from '@/api/manufacturing.service';

interface ProductionJobFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  isLoading?: boolean;
}

const ProductionJobForm: React.FC<ProductionJobFormProps> = ({ initialData, onSubmit, isLoading = false }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    jobNumber: '',
    recipeId: '',
    recipeName: '',
    plannedQuantity: 1,
    priority: 'medium',
    scheduledStartDate: '',
    scheduledEndDate: '',
    assignedTo: '',
    notes: '',
    status: 'planned'
  });
  
  const [recipes, setRecipes] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    fetchRecipes();
    fetchUsers();
    if (initialData) {
      setFormData({
        jobNumber: initialData.jobNumber || '',
        recipeId: initialData.recipeId || '',
        recipeName: initialData.recipeName || '',
        plannedQuantity: initialData.plannedQuantity || 1,
        priority: initialData.priority || 'medium',
        scheduledStartDate: initialData.scheduledStartDate || '',
        scheduledEndDate: initialData.scheduledEndDate || '',
        assignedTo: initialData.assignedTo || '',
        notes: initialData.notes || '',
        status: initialData.status || 'planned'
      });
    }
  }, [initialData]);

  const fetchRecipes = async () => {
    try {
      const response = await ManufacturingService.getRecipes(businessId);
      if (response.data?.success) {
        setRecipes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await ManufacturingService.getUsers(businessId);
      if (response.data?.success) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.jobNumber || !formData.recipeId || !formData.plannedQuantity) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all required fields",
      });
      return;
    }

    onSubmit(formData);
  };

  const handleRecipeChange = (recipeId: string) => {
    const selectedRecipe = recipes.find(recipe => recipe.id.toString() === recipeId);
    setFormData(prev => ({
      ...prev,
      recipeId,
      recipeName: selectedRecipe?.name || ''
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Production Job Details</CardTitle>
          <CardDescription>Basic information about the production job</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="jobNumber">Job Number *</Label>
              <Input
                id="jobNumber"
                value={formData.jobNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, jobNumber: e.target.value }))}
                placeholder="Enter job number"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipeId">Recipe *</Label>
              <Select value={formData.recipeId} onValueChange={handleRecipeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select recipe" />
                </SelectTrigger>
                <SelectContent>
                  {recipes.map((recipe) => (
                    <SelectItem key={recipe.id} value={recipe.id.toString()}>
                      {recipe.name} ({recipe.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="plannedQuantity">Planned Quantity *</Label>
              <Input
                id="plannedQuantity"
                type="number"
                value={formData.plannedQuantity}
                onChange={(e) => setFormData(prev => ({ ...prev, plannedQuantity: parseFloat(e.target.value) || 1 }))}
                min="0.01"
                step="0.01"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduledStartDate">Scheduled Start Date</Label>
              <Input
                id="scheduledStartDate"
                type="datetime-local"
                value={formData.scheduledStartDate}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduledStartDate: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scheduledEndDate">Scheduled End Date</Label>
              <Input
                id="scheduledEndDate"
                type="datetime-local"
                value={formData.scheduledEndDate}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduledEndDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignedTo">Assigned To</Label>
            <Select value={formData.assignedTo} onValueChange={(value) => setFormData(prev => ({ ...prev, assignedTo: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id.toString()}>
                    {user.name} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes or instructions"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-4">
        <Button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
          {initialData ? 'Update Production Job' : 'Create Production Job'}
        </Button>
      </div>
    </form>
  );
};

export default ProductionJobForm; 